import { useState } from 'react';
import { Button, Dropdown, Table, Tag, Typography } from 'antd';
import { BoxArrowDown, DotsThree, Note, Trash } from '@phosphor-icons/react';
import styled from 'styled-components';
import { ConfirmationPopup, ItemImage, OrganisationAvatar } from '../../components';
import { BoldText, MuteText } from '../../elements';
import { InventoryTransferListHook } from '../../hooks/inventory';
import {
  CommonUtility,
  DateFormat,
  DateUtility,
  InventoryAdjustmentTypes,
  InventoryService,
  ToastMessage,
  theme,
} from '../../utility';

const { Text } = Typography;

const SCHEDULED_TRANSFER_TABLE_SCROLL_WIDTH = 1320;

const statusColors = {
  pending: theme.colors.darkBlue,
};

const priorityColors = {
  medium: theme.colors.yellow,
};

const ItemTitle = styled.div`
  .title {
    max-width: 12vw;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NoteContainer = styled.div`
  .note-icon {
    min-width: 21px;
  }
`;

const ReasonClip = styled.span`
  display: block;
  white-space: nowrap;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ScheduledTransferTable = styled(Table)`
  .scheduled-transfer-action-cell,
  .ant-table-cell-fix-right {
    background: ${({ theme }) => theme.colors.white};
    z-index: 3;
  }

  .ant-table-tbody > tr:hover > .scheduled-transfer-action-cell,
  .ant-table-tbody > tr:hover > .ant-table-cell-fix-right {
    background: ${({ theme }) => theme.colors.box};
  }
`;

function inventoryTransferCancellationAllowed(item) {
  if (!item?.status) {
    return false;
  }

  const { status } = item;
  return status !== 'completed' && status !== 'cancelled';
}

export function InventoryTransferListScreen({ warehouseId }) {
  const [transferToCancel, setTransferToCancel] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const {
    data,
    loading,
    totalData,
    filter,
    filterChanged,
    pageChanged,
    refresh,
    setLoading,
  } = InventoryTransferListHook(warehouseId);

  const columns = [
    {
      title: 'Product Description',
      key: 'description',
      width: 280,
      render: (_, item) => (
        <div className="d-flex align-items-center">
          <ItemImage
            imageKey="images"
            displayImageKey="display_image"
            item={item.product}
            size="mini"
          />
          <ItemTitle className="ml-4">
            <div className="title">{item.product?.name || '—'}</div>
            <div className="d-flex">
              <MuteText className="mr-2">SKU</MuteText>
              <BoldText>{item.product?.sku || '—'}</BoldText>
            </div>
          </ItemTitle>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 135,
      render: (_, item) => (
        <div>
          <Tag color={statusColors[item.status]}>
            {CommonUtility.removeUnderscoreAndCapitalize(item.status || '—')}
          </Tag>
          <br />
          <Tag color={priorityColors[item.priority]} className="mt-1">
            {CommonUtility.toTitleCase(item.priority || '—')}
          </Tag>
        </div>
      ),
    },
    {
      title: 'From',
      key: 'from',
      width: 150,
      render: (_, item) => (
        <div>
          <Tag color={item.sourceLocation?.zone?.color}>
            {item.sourceLocation?.zone?.code || '—'}
          </Tag>
          <br />
          <Tag color={item.sourceLocation?.zone?.color} className="mt-1">
            {item.sourceLocation?.code || '—'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'To',
      key: 'to',
      width: 150,
      render: (_, item) => (
        <div>
          <Tag color={item.destinationLocation?.zone?.color}>
            {item.destinationLocation?.zone?.code || '—'}
          </Tag>
          <br />
          <Tag color={item.destinationLocation?.zone?.color} className="mt-1">
            {item.destinationLocation?.code || '—'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Restock Amount',
      key: 'amount',
      width: 150,
      render: (_, item) => (
        <>
          <div>
            <Text>{item.quantity}</Text>
            <MuteText className="ml-1">Units</MuteText>
          </div>
          {item.quantityCartons ? (
            <div>
              <Text>{item.quantityCartons}</Text>
              <MuteText className="ml-1">Cartons</MuteText>
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: 'Assignee',
      key: 'assignee',
      width: 150,
      render: (_, item) => (
        <div className="d-flex align-items-center">
          <OrganisationAvatar name={item.initiatedBy?.firstName} />
          <span className="ml-1">{item.initiatedBy?.firstName || '—'}</span>
        </div>
      ),
    },
    {
      title: 'Date Created',
      key: 'date_created',
      width: 120,
      render: (_, item) =>
        item.createdAt
          ? DateUtility.formatDate(item.createdAt, DateFormat.barChartMonth)
          : '—',
    },
    {
      title: 'Note',
      key: 'note',
      width: 170,
      render: (_, item) => (
        <NoteContainer className="d-flex align-items-center">
          <Note className="note-icon" size={21} color={theme.colors.grey} />
          <div className="d-flex flex-column ml-2">
            <ReasonClip title={item.reason}>{item.reason || '—'}</ReasonClip>
            <MuteText>{item.initiatedBy?.firstName || '—'}</MuteText>
          </div>
        </NoteContainer>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 115,
      align: 'right',
      fixed: 'end',
      className: 'scheduled-transfer-action-cell',
      render: (_, item) => {
        const menuItems = [
          {
            key: 'complete',
            label: 'Complete Transfer',
            icon: <BoxArrowDown size={14} />,
            onClick: () => completeTransfer(item),
          },
        ];

        if (inventoryTransferCancellationAllowed(item)) {
          menuItems.push({
            key: 'cancel',
            label: 'Cancel transfer',
            icon: <Trash size={14} />,
            danger: true,
            onClick: () => setTransferToCancel(item),
          });
        }

        return (
          <ActionButtons>
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button
                icon={<DotsThree size={18} weight="bold" />}
                aria-label="Transfer actions"
              />
            </Dropdown>
          </ActionButtons>
        );
      },
    },
  ];

  const completeTransfer = async item => {
    try {
      setLoading(true);
      const payload = {
        transferId: item.transferId,
        quantity: item.quantity,
        adjustmentType: InventoryAdjustmentTypes.completeTransfer,
        destinationLocationId: item.destinationLocation._id,
      };

      await InventoryService.updateInventory(item.inventoryId, payload);
      refresh();
      ToastMessage.success('Inventory transferred successfully');
    } catch (error) {
      ToastMessage.apiError(error);
      setLoading(false);
    }
  };

  const confirmCancelTransfer = async () => {
    if (!transferToCancel?.transferId) {
      return;
    }
    try {
      setCancelSubmitting(true);
      await InventoryService.cancelInventoryTransfer(transferToCancel.transferId);
      ToastMessage.success('Transfer cancelled');
      setTransferToCancel(null);
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const pagination = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50, 100],
    showTotal: total => `${total} scheduled transfers`,
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <div>
      <ScheduledTransferTable
        rowKey={item => item.transferId || item._id}
        columns={columns}
        dataSource={data}
        loading={loading}
        size="middle"
        tableLayout="fixed"
        pagination={pagination}
        scroll={{ x: SCHEDULED_TRANSFER_TABLE_SCROLL_WIDTH }}
        locale={{ emptyText: 'No scheduled transfers' }}
        style={{
          borderRadius: 6,
          border: `1px solid ${theme.colors.semanticBorder}`,
        }}
      />

      <ConfirmationPopup
        open={!!transferToCancel}
        onClose={() => !cancelSubmitting && setTransferToCancel(null)}
        onConfirm={confirmCancelTransfer}
        loading={cancelSubmitting}
        title="Cancel transfer?"
        message="Inventory will be released and this transfer will be cancelled."
        closeText="Keep transfer"
        confirmText="Cancel transfer"
      />
    </div>
  );
}
